import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';

// Adjust the import path to match your project structure.
// Assumes these are exported from the same module.
import useUserApi, {
  User,
  UserEventFrequencies,
  UserEventType,
  UserRoles,
} from '../../managers/UserManager';

const USAGE_UNLIMITED = -1;

interface UserUsageProps {
  userId: string;
  backendUser?: User; // optional, since you can fetch
  eventType: UserEventType;
  frequencies: UserEventFrequencies[];
}

const UserUsage: React.FC<UserUsageProps> = ({ userId, backendUser, eventType, frequencies }) => {
  const { getUser, hasAnyRole } = useUserApi();
  const [user, setUser] = useState<User | null>(backendUser ?? null);
  const [loading, setLoading] = useState<boolean>(!backendUser);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchUser = async () => {
      if (backendUser) {
        setUser(backendUser);
        setLoading(false);
        return;
      }

      try {
        const userData = await getUser(userId);
        if (isMounted) {
          setUser(userData);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load user usage data:', err);
        if (isMounted) {
          setError('Failed to load usage info.');
          setLoading(false);
        }
      }
    };

    fetchUser();
    return () => {
      isMounted = false;
    };
  }, [userId, backendUser, getUser]);

  if (loading) return <p>Loading usage...</p>;
  if (error) return <p className="text-danger">{error}</p>;
  if (!user) return <p className="text-muted">No user data.</p>;

  const isUnlimitedUser = hasAnyRole(user, [UserRoles.SUPER_ADMIN, UserRoles.CREATOR, UserRoles.FRIENDS_FAMILY]);

  const usage = (user?.usage?.[eventType] ?? {}) as Partial<
    Record<UserEventFrequencies, { used: number; limit?: number; resetDate?: string }>
  >;

  const formatReset = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : null);

  const creditPool = user?.creditPool ?? 0;

  return (
    <div>
      <h4>AI Image Limits</h4>
      {
        frequencies.map((frequency) => {
          const credit = usage[frequency];

          if (!credit) {
            return (
              <p key={frequency} className="text-muted text-capitalize">
                {frequency}: no usage recorded
              </p>
            );
          }

          const safeLimit = typeof credit.limit === 'number' && credit.limit > 0 ? credit.limit : 0;
          const percentUsed =
            isUnlimitedUser || credit.limit == null || safeLimit === 0
              ? 0
              : Math.min(100, (credit.used / safeLimit) * 100);

          const resetText = formatReset(credit.resetDate);

          return (
            <div key={frequency} className="mb-3">
              <div className="d-flex align-items-center gap-2">
                {/* Label */}
                <div className="text-capitalize" style={{ minWidth: '80px' }}>
                  <strong>{frequency}:</strong>
                </div>

                {/* Usage + Info */}
                <>
                  <div
                    className="d-flex align-items-center text-nowrap"
                    style={{ minWidth: '110px' }}
                  >
                    <small>
                      {credit.used} / {isUnlimitedUser || credit.limit == null ? "∞" : safeLimit}
                    </small>
                    {resetText && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`tooltip-${frequency}`}>
                            Resets at: {resetText}
                          </Tooltip>
                        }
                      >
                        <span
                          className="ms-1 text-muted"
                          role="button"
                          aria-label="Reset info"
                        >
                          <FaInfoCircle size={12} />
                        </span>
                      </OverlayTrigger>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="progress flex-grow-1" style={{ height: '20px' }}>
                    <div
                      className={`progress-bar ${percentUsed >= 100 ? 'bg-danger' : 'bg-success'
                        }`}
                      role="progressbar"
                      style={{ width: `${percentUsed}%` }}
                      aria-valuenow={credit.used}
                      aria-valuemin={0}
                      aria-valuemax={safeLimit}
                    />
                  </div>
                </>
              </div>
            </div>
          );
        })
      }

      {/* Bonus Credit Pool */}
      {!isUnlimitedUser && (
        <div className="mb-3 pt-3 border-top">
          <div className="d-flex align-items-center gap-2">
            {/* Label */}
            <div style={{ minWidth: '80px' }}>
              <strong>Bonus Credits:</strong>
            </div>

            {/* Credit Pool Count + Info */}
            <div
              className="d-flex align-items-center text-nowrap"
              style={{ minWidth: '110px' }}
            >
              <small>{creditPool} remaining</small>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-credit-pool">
                    Bonus credits are used when time-based limits are exceeded
                  </Tooltip>
                }
              >
                <span
                  className="ms-1 text-muted"
                  role="button"
                  aria-label="Bonus credits info"
                >
                  <FaInfoCircle size={12} />
                </span>
              </OverlayTrigger>
            </div>

            {/* Visual indicator */}
            <div className="flex-grow-1">
              <span className={`badge ${creditPool > 0 ? 'bg-primary' : 'bg-secondary'}`}>
                {creditPool > 0 ? `${creditPool} available` : 'None available'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default UserUsage;
